import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Input, InputNumber, Select, Space, Switch, Tag, message } from "antd";
import {
  ApiOutlined,
  DisconnectOutlined,
  ExperimentOutlined,
  CopyOutlined,
  ClearOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  SCALE_BAUD_RATES,
  connectScaleSerial,
  isWebSerialSupported,
  parseScaleWeight,
  startScaleSimulator,
} from "./lib/scaleSerial";
import "./ScaleDemo.css";

const ScaleDemo = () => {
  const supported = isWebSerialSupported();
  const [baudRate, setBaudRate] = useState(9600);
  const [connected, setConnected] = useState(false);
  const [simMode, setSimMode] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [rawLine, setRawLine] = useState("");
  const [log, setLog] = useState([]);
  const [kg, setKg] = useState(null);
  const [stable, setStable] = useState(false);
  const [unitHint, setUnitHint] = useState("");
  const [portInfo, setPortInfo] = useState(null);
  const [manualKg, setManualKg] = useState(null);

  const sessionRef = useRef(null);
  const simRef = useRef(null);

  const pushLog = useCallback((line) => {
    setLog((prev) => [{ at: new Date().toLocaleTimeString(), line }, ...prev].slice(0, 40));
  }, []);

  const handleLine = useCallback((line) => {
    setRawLine(line);
    pushLog(line);
    const parsed = parseScaleWeight(line);
    if (!parsed) return;
    setKg(parsed.kg);
    setStable(!!parsed.stable);
    setUnitHint(parsed.unit || "");
  }, [pushLog]);

  const disconnect = useCallback(async () => {
    if (simRef.current) {
      simRef.current.stop();
      simRef.current = null;
    }
    if (sessionRef.current) {
      try {
        await sessionRef.current.close();
      } catch { /* ignore */ }
      sessionRef.current = null;
    }
    setConnected(false);
    setSimMode(false);
    setPortInfo(null);
  }, []);

  useEffect(() => () => { disconnect(); }, [disconnect]);

  const connectReal = async () => {
    if (!supported) {
      message.error("Use Chrome or Edge on desktop for USB/COM scales");
      return;
    }
    setConnecting(true);
    try {
      await disconnect();
      const session = await connectScaleSerial({
        baudRate,
        onLine: handleLine,
        onError: (err) => {
          message.error(err?.message || "Scale read error");
        },
        onDisconnect: () => {
          setConnected(false);
          sessionRef.current = null;
        },
      });
      sessionRef.current = session;
      setPortInfo(session.getInfo());
      setConnected(true);
      setSimMode(false);
      message.success("Scale connected — place weight on the platter");
    } catch (err) {
      if (err?.name !== "NotFoundError") {
        message.error(err?.message || "Could not open scale port");
      }
    } finally {
      setConnecting(false);
    }
  };

  const connectSimulator = async () => {
    await disconnect();
    simRef.current = startScaleSimulator({ onLine: handleLine });
    setConnected(true);
    setSimMode(true);
    setPortInfo({ simulated: true });
    message.success("Simulator running (no hardware)");
  };

  const copyKg = async () => {
    const v = kg != null ? String(Number(kg.toFixed(3))) : "";
    if (!v) {
      message.warning("No weight yet");
      return;
    }
    try {
      await navigator.clipboard.writeText(v);
      message.success(`Copied ${v} kg`);
    } catch {
      message.info(v);
    }
  };

  const displayKg = kg != null ? kg.toFixed(3) : "—";

  return (
    <div className="scale-demo">
      <header className="scale-demo__hero">
        <div>
          <div className="scale-demo__eyebrow">Hardware demo</div>
          <h1>Scale connection</h1>
          <p>
            Connect a USB / COM digital scale (Web Serial) and read live kg.
            After this works, we can push the weight into the POS cart automatically.
          </p>
        </div>
        <div className="scale-demo__status">
          {!supported && (
            <Tag color="red" icon={<WarningOutlined />}>Chrome / Edge required</Tag>
          )}
          {supported && !connected && (
            <Tag color="default">Not connected</Tag>
          )}
          {connected && (
            <Tag color={simMode ? "purple" : "green"} icon={<CheckCircleOutlined />}>
              {simMode ? "Simulator" : "Scale live"}
            </Tag>
          )}
        </div>
      </header>

      <div className="scale-demo__grid">
        <section className="scale-demo__card scale-demo__readout">
          <div className="scale-demo__label">Live weight</div>
          <div className={`scale-demo__kg${stable ? " is-stable" : ""}`}>
            {displayKg}
            <span>kg</span>
          </div>
          <div className="scale-demo__meta">
            {kg == null && <span>Waiting for data…</span>}
            {kg != null && (
              <>
                <span className={stable ? "ok" : "warn"}>
                  {stable ? "Stable" : "Unstable / settling"}
                </span>
                {unitHint ? <span>· parsed as {unitHint}</span> : null}
              </>
            )}
          </div>
          <Space wrap style={{ marginTop: 16 }}>
            <Button icon={<CopyOutlined />} type="primary" disabled={kg == null} onClick={copyKg}>
              Copy kg
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={() => {
                setKg(null);
                setRawLine("");
                setStable(false);
              }}
            >
              Clear
            </Button>
          </Space>
        </section>

        <section className="scale-demo__card">
          <div className="scale-demo__label">Connection</div>
          <div className="scale-demo__field">
            <span>Baud rate</span>
            <Select
              style={{ width: "100%" }}
              value={baudRate}
              disabled={connected}
              options={SCALE_BAUD_RATES.map((b) => ({ value: b, label: String(b) }))}
              onChange={setBaudRate}
            />
          </div>
          <div className="scale-demo__field" style={{ marginTop: 12 }}>
            <span>Web Serial supported</span>
            <Switch checked={supported} disabled />
          </div>
          {portInfo && (
            <div className="scale-demo__port">
              {portInfo.simulated
                ? "Source: built-in simulator"
                : `USB VID ${portInfo.usbVendorId ?? "—"} · PID ${portInfo.usbProductId ?? "—"}`}
            </div>
          )}
          <Space wrap style={{ marginTop: 18 }}>
            {!connected ? (
              <>
                <Button
                  type="primary"
                  icon={<ApiOutlined />}
                  loading={connecting}
                  disabled={!supported}
                  onClick={connectReal}
                >
                  Connect scale
                </Button>
                <Button icon={<ExperimentOutlined />} onClick={connectSimulator}>
                  Use simulator
                </Button>
              </>
            ) : (
              <Button danger icon={<DisconnectOutlined />} onClick={disconnect}>
                Disconnect
              </Button>
            )}
          </Space>
          <ol className="scale-demo__steps">
            <li>Plug the scale USB / serial adapter into this PC.</li>
            <li>Click <b>Connect scale</b> and pick the COM / USB device.</li>
            <li>If nothing appears, try another baud rate (often 9600 or 4800).</li>
            <li>Use <b>Simulator</b> first if you only want to test the UI.</li>
          </ol>
        </section>

        <section className="scale-demo__card">
          <div className="scale-demo__label">Raw line from scale</div>
          <Input.TextArea
            value={rawLine}
            readOnly
            rows={3}
            placeholder="e.g. ST,GS,+012.450kg"
            className="scale-demo__raw"
          />
          <div className="scale-demo__label" style={{ marginTop: 16 }}>Manual parse test</div>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              placeholder="Paste a scale line to test parser"
              onPressEnter={(e) => handleLine(e.target.value)}
              onBlur={(e) => { if (e.target.value.trim()) handleLine(e.target.value); }}
            />
          </Space.Compact>
          <div className="scale-demo__label" style={{ marginTop: 16 }}>Or type kg manually</div>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            step={0.001}
            value={manualKg}
            onChange={(v) => {
              setManualKg(v);
              if (v != null) {
                setKg(Number(v));
                setStable(true);
                setUnitHint("manual");
              }
            }}
          />
        </section>

        <section className="scale-demo__card scale-demo__log">
          <div className="scale-demo__label">Recent lines</div>
          {log.length === 0 && <div className="scale-demo__empty">No data yet</div>}
          <ul>
            {log.map((row, i) => (
              <li key={`${row.at}-${i}`}>
                <time>{row.at}</time>
                <code>{row.line}</code>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
};

export default ScaleDemo;
